// PounceLeads.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce agent page ka "Leads" hissa: har lead, uska status aur calls. Row ka
// apna component hai (PounceLeadRow); lead source (webhook) PounceLeadSource.jsx
// me hai. Lead haath se jodna upar ke "Add Lead Manually" button se hota hai
// (PounceAddLead).
//
// Live = socket ka "pounce_lead_updated" (usePounceLeadEvents).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addPounceLead,getPounceLeads } from "../../../Store/apiStore";
import styles from "./PounceLeads.module.css";
import PounceLeadRow from "./PounceLeadRow";
import { usePounceLeadEvents } from "../../PounceLeadsPage/pounceLeadShared";

// Purane import chalte rahen: `import { PounceLeadSource } from "./PounceLeads"`.
export { default as PounceLeadSource } from "./PounceLeadSource";

const EMPTY_FORM = { phone: "", name: "", inquiry: "" };
// Agent page pe sirf taaza leads; poori list "View all" se (/leads).
const PREVIEW_COUNT = 5;
const SKELETON_ROWS = [0, 1, 2];

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

const PounceLeads = ({ onChange }) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // { text, tone: "success" | "error" } — tone se banner ka rang tay hota hai.
  const [message, setMessage] = useState(null);
  const load = useCallback(() => {
    getPounceLeads({ limit: 50 })
      .then(setLeads)
      .catch(() => setLeads((prev) => prev || []));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  usePounceLeadEvents(load);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      // consent: the required checkbox in the form is the only way to reach submit.
      const res = await addPounceLead({ ...form, consent: true, consent_source: "manual" });
      setMessage({
        text: res?.duplicate
          ? "That lead is already being handled."
          : res?.paused
            ? "Lead added — Outbound Caller is off, so it's on hold."
            : "Lead added — calling now.",
        tone: "success",
      });
      setForm(EMPTY_FORM);
      setAdding(false);
      load();
      onChange?.();
    } catch (err) {
      setMessage({
        text: errorText(err, "We couldn't add that lead. Please try again."),
        tone: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.section} aria-labelledby="pounce-leads-title">
      <div className={styles.head}>
        <h3 id="pounce-leads-title" className={styles.title}>
          Leads
          {leads?.length > 0 && <span className={styles.count}>{leads.length}</span>}
        </h3>
        {leads?.length > PREVIEW_COUNT && (
          <button type="button" className={styles.viewAll} onClick={() => navigate("/leads")}>
            View all
          </button>
        )}
      </div>

      {leads === null ? (
        <ul className={styles.list} aria-busy="true" aria-label="Loading leads">
          {SKELETON_ROWS.map((i) => (
            <li key={i} className={styles.skeletonCard}>
              <span className={`${styles.shimmer} ${styles.skeletonAvatar}`} />
              <span className={styles.skeletonText}>
                <span className={`${styles.shimmer} ${styles.skeletonLine}`} />
                <span className={`${styles.shimmer} ${styles.skeletonLine} ${styles.skeletonLineShort}`} />
              </span>
              <span className={`${styles.shimmer} ${styles.skeletonPill}`} />
            </li>
          ))}
        </ul>
      ) : leads.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No leads yet</p>
          <p className={styles.emptyText}>
            Add one with “Add Lead Manually”, or connect a lead source and Outbound Caller will call each lead
            within seconds.
          </p>
        </div>
      ) : (
        <ul className={styles.list}>
          {leads.slice(0, PREVIEW_COUNT).map((lead) => (
            <PounceLeadRow key={lead.id} lead={lead} />
          ))}
        </ul>
      )}
    </section>
  );
};

export default PounceLeads;
